/* eslint-disable react/no-unknown-property */
import { fetchCustomers, getCustomers } from 'admin/actions/customers';
import CSVDownloadButton from 'admin/components/atoms/CSVDownloadButton';
import Breadcrumb from 'admin/components/shared/Breadcrumb';
import { toQueryParams } from 'admin/utils';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  BooleanParam,
  JsonParam,
  NumberParam,
  QueryParamConfig,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { useAppDispatch, useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import { getQueryParamDefaultValues } from '../../../utils/use-query-params';
import Headings from '../../atoms/Headings';
import Loader from '../../atoms/Loader';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import SeqPager from '../../organisms/SeqPager';
import { toCsvRow } from './csv';
import CustomerListTable from './CustomerListTable';
import FilterForm from './FilterForm';

const SearchResultInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
`;
const SearchResultTitle = styled(Headings.h5)`
  margin: 0;
  margin-left: 15px;
`;
const SearchResultCount = styled.span`
  flex: 1;
`;

const LoaderWrapper = styled.div`
  width: 100%;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const createOptionParam = <T extends readonly string[]>(
  options: T
): QueryParamConfig<T[number] | null> => ({
  encode: (value) => (options.find((x) => x === value) ? value : null),
  decode: (value) =>
    typeof value === 'string'
      ? options.find((x) => x === value)
        ? (value as typeof options[number])
        : null
      : null,
});

const MomentParam: QueryParamConfig<Moment | null> = {
  encode: (value) => (value ? value.toISOString() : null),
  decode: (value) => (value && typeof value === 'string' ? moment(value) : null),
};

const SORT_BY_OPTIONS = ['last-transaction-done-at', 'registered-at'] as const;
const SORT_DIRECTION_OPTIONS = ['asc', 'desc'] as const;
const INDIVIDUAL_NUMBER_IDENTIFICATION_OPTIONS = ['identified', 'not-identified'] as const;
// 住民判定は「住民 / 非住民 / 判定なし」の3値。非住民 (判定済みで住民でない) と
// 判定なし (まだ判定していない) は運用上まったく別の意味なので boolean にしない
const RESIDENT_STATUS_OPTIONS = ['resident', 'non-resident', 'unknown'] as const;

type Config = typeof queryParamConfigs;
export type QueryParams = {
  [key in keyof Config]: Config[key] extends QueryParamConfig<unknown, infer T> ? T : never;
};

const queryParamConfigs = {
  customer_id: withDefault(StringParam, ''),
  customer_tel: withDefault(StringParam, ''),
  customer_email: withDefault(StringParam, ''),
  customer_name: withDefault(StringParam, ''),
  external_id: withDefault(StringParam, ''),
  registered_from: withDefault(MomentParam, null),
  registered_to: withDefault(MomentParam, null),
  last_transaction_from: withDefault(MomentParam, null),
  last_transaction_to: withDefault(MomentParam, null),
  user_attribute_queries: withDefault(JsonParam, {}),
  is_include_user_attribute: withDefault(BooleanParam, true),
  per_page: withDefault(NumberParam, 50),
  sort_by: withDefault(createOptionParam(SORT_BY_OPTIONS), 'registered-at'),
  sort_direction: withDefault(createOptionParam(SORT_DIRECTION_OPTIONS), 'desc'),
  account_status: withDefault(StringParam, null),
  individual_number_identification: withDefault(
    createOptionParam(INDIVIDUAL_NUMBER_IDENTIFICATION_OPTIONS),
    null
  ),
  resident_status: withDefault(createOptionParam(RESIDENT_STATUS_OPTIONS), null),
};

const queryParamDefaultValues = getQueryParamDefaultValues(queryParamConfigs);

const toQueryParamsUserAttributeQueries = (
  params: {
    [key in keyof Partial<typeof queryParamConfigs>]: unknown;
  } & { private_money_id: string; fast_pagination: boolean }
) => {
  // user_attribute_queriesが{}だとtoQueryParamsでの変換後に?user_attribute_queries[]=となるが
  // この値はサーバ側で空文字列と解釈されてしまいバリデーションエラーになるのでnullに置き換える
  // nullに置き換えることでtoQueryParamsの返り値に含まれなくなり、バリデーションエラーの問題を回避できる

  const copiedParams = { ...params };
  const { user_attribute_queries } = params;

  if (user_attribute_queries && Object.keys(user_attribute_queries).length === 0) {
    Object.assign(copiedParams, { user_attribute_queries: null });
  }

  return toQueryParams(copiedParams, true);
};

const removeNullKeys = (obj: Record<PropertyKey, unknown>) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null));

//* Component Definition
const Customers = () => {
  const { user, customers, isLoading } = useAppSelector((state) => ({
    user: state.user,
    customers: state.customer.customers,
    isLoading: state.customer.isLoading,
  }));
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  const dispatch = useAppDispatch();

  const allowedPrivateMoneys = user.isServiceUser() ? [] : user.issuedMoneys();
  const isSelectedMoneyAllowed =
    user.isServiceUser() || allowedPrivateMoneys.some((m) => m.id === userPrivateMoney.id);

  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const [state, setState] = useState<QueryParams>({
    ...query,
  });

  const getCustomerList = useCallback(
    (queryLink = '') => {
      if (isSelectedMoneyAllowed) {
        const queryString = !!queryLink
          ? queryLink
          : toQueryParamsUserAttributeQueries({
              ...query,
              private_money_id: userPrivateMoney.id,
              fast_pagination: true,
            });

        dispatch(getCustomers(queryString));
        setState((prev) => ({ ...prev, ...query }));
      }
    },
    [query, dispatch, userPrivateMoney.id, isSelectedMoneyAllowed]
  );

  useEffect(() => {
    getCustomerList();
  }, [query, dispatch, userPrivateMoney.id, isSelectedMoneyAllowed]);

  const onSearch = () => {
    const { user_attribute_queries, ...rest } = state;
    const nonNullUserAttributeQueries = removeNullKeys(user_attribute_queries) ?? {};
    setQuery({ ...rest, user_attribute_queries: nonNullUserAttributeQueries });
  };

  const onClear = () => {
    setState({ ...query });
    setQuery({ ...queryParamDefaultValues });
  };

  const onChangeParams = (params: Partial<QueryParams>) =>
    setState((prev) => ({ ...prev, ...params }));

  const onChangePage = (paginatedQuery: string | null) =>
    getCustomerList(paginatedQuery ?? undefined);

  const onChangeQuery = (key: PropertyKey, value: unknown) => {
    setState((prev) => ({
      ...prev,
      user_attribute_queries: { ...prev.user_attribute_queries, [key]: value },
    }));
  };

  const onChangeSorting = ({
    sortBy,
    sortDirection,
  }: {
    sortBy: typeof SORT_BY_OPTIONS[number];
    sortDirection: typeof SORT_DIRECTION_OPTIONS[number];
  }) => {
    setQuery((prev) => ({ ...prev, sort_by: sortBy, sort_direction: sortDirection }));
  };

  const crumb = [
    { value: 'ホーム', href: '/', active: false },
    { value: 'お客様管理', href: '', active: true },
  ];

  const numberOfDataMax = 30000;

  return (
    <div>
      <Breadcrumb items={crumb} />
      {!isSelectedMoneyAllowed && (
        <>
          <SelectedPrivateMoneyDisplay
            highlightHeader
            allowedPrivateMoneys={allowedPrivateMoneys}
          />
          <UserPrivateMoneyNotAllowed />
        </>
      )}
      {isSelectedMoneyAllowed && (
        <>
          <FilterForm
            filterParams={state}
            onChangeParams={onChangeParams}
            onSearch={onSearch}
            onClear={onClear}
            onChangeQuery={onChangeQuery}
            selectedMoney={userPrivateMoney}
          />
          <SearchResultInfo>
            <SearchResultTitle>検索結果</SearchResultTitle>
            {customers && !!customers.count && (
              <>
                <SearchResultCount>
                  件数：{customers.count} (最大{numberOfDataMax}件までダウンロードできます)
                </SearchResultCount>
                <CSVDownloadButton
                  csvFilePrefix="customers_"
                  fetchData={async () => {
                    const searchQuery = toQueryParamsUserAttributeQueries({
                      ...state,
                      is_include_user_attribute: true,
                      per_page: numberOfDataMax,
                      private_money_id: userPrivateMoney.id,
                      fast_pagination: true,
                    });

                    const fetchedCustomers = await fetchCustomers(searchQuery);

                    const formattedCustomers = fetchedCustomers.rows.map(toCsvRow);

                    return formattedCustomers;
                  }}
                />
              </>
            )}
          </SearchResultInfo>
          {isLoading ? (
            <LoaderWrapper>
              <Loader type="Oval" width={50} height={50} />
            </LoaderWrapper>
          ) : (
            <CustomerListTable
              customers={customers?.rows ?? []}
              sortOptions={{
                sortBy: query.sort_by ?? 'registered-at',
                sortDirection: query.sort_direction ?? 'desc',
              }}
              onChangeSorting={onChangeSorting}
            />
          )}
          {/* <Pager pagination={customers.pagination} onChangePage={onChangePage} /> */}
          {customers && (
            <SeqPager prev={customers.prev} next={customers.next} onChangePage={onChangePage} />
          )}
        </>
      )}
    </div>
  );
};

export default Customers;
